import { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload, faXmark,
  faFilePdf, faFileImage, faFileWord, faFileExcel, faFile,
} from "@fortawesome/free-solid-svg-icons";
import CrudApi from "@serie3/common/api/crudApi";
import downloadHandler from "@serie3/freelancesplaceapi/Domain/Shared/downloadHandler";
import DocumentsConfig from "@serie3/freelancesplaceapi/Config/Documents/DocumentsConfig";

const extIcon = (name = "") => {
  const ext = name.split(".").pop().toLowerCase();
  if (ext === "pdf") return { icon: faFilePdf, color: "#c62828", bg: "#ffebee" };
  if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext))
    return { icon: faFileImage, color: "#1565c0", bg: "#e3f2fd" };
  if (["doc", "docx"].includes(ext))
    return { icon: faFileWord, color: "#1a73e8", bg: "#e8f0fe" };
  if (["xls", "xlsx"].includes(ext))
    return { icon: faFileExcel, color: "#2e7d32", bg: "#e8f5e9" };
  return { icon: faFile, color: "#757575", bg: "#f5f5f5" };
};

const isImage = (name = "") =>
  ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(name.split(".").pop().toLowerCase());

const isPdf = (name = "") =>
  name.split(".").pop().toLowerCase() === "pdf";

const FilePreviewPanel = ({ attachment, onClose }) => {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const blobRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setLoading(true);
    const load = async () => {
      const res = await CrudApi.getBinary(
        DocumentsConfig.endpoints.preview + attachment.fileId
      );
      if (cancelled) return;
      const blob = new Blob([res.data], {
        type: res.headers?.["content-type"] || "application/octet-stream",
      });
      const objectUrl = URL.createObjectURL(blob);
      blobRef.current = objectUrl;
      setUrl(objectUrl);
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
      if (blobRef.current) { URL.revokeObjectURL(blobRef.current); blobRef.current = null; }
    };
  }, [attachment.fileId]);

  const { icon, color, bg } = extIcon(attachment.originalName);

  return (
    <div style={{
      width: "420px",
      flexShrink: 0,
      marginLeft: "20px",
      borderLeft: "1px solid #e0e0e0",
      paddingLeft: "20px",
      display: "flex",
      flexDirection: "column",
      gap: "12px",
      minHeight: 0,
      overflow: "hidden",
    }}>
      {/* Panel header — same structure as AI panel */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "7px", minWidth: 0 }}>
          <div style={{
            width: "26px", height: "26px", borderRadius: "7px",
            background: bg, display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <FontAwesomeIcon icon={icon} style={{ fontSize: "0.7rem", color }} />
          </div>
          <span style={{
            fontWeight: 700, fontSize: "0.88rem", color: "#212121",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {attachment.originalName}
          </span>
        </div>
        <div style={{ display: "flex", gap: "6px", alignItems: "center", flexShrink: 0, marginLeft: "8px" }}>
          <button
            onClick={() => downloadHandler(
              DocumentsConfig.endpoints.preview + attachment.fileId,
              attachment.originalName
            )}
            style={{
              display: "inline-flex", alignItems: "center", gap: "5px",
              padding: "5px 10px", background: "#e8f0fe", color: "#1a73e8",
              border: "none", borderRadius: "6px", fontWeight: 600,
              fontSize: "0.75rem", cursor: "pointer",
            }}
          >
            <FontAwesomeIcon icon={faDownload} />
          </button>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#9e9e9e", padding: "4px" }}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      </div>

      {/* Preview content */}
      <div style={{
        flex: 1, minHeight: 0, borderRadius: "10px", overflow: "hidden",
        background: "#f8f9fb", display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {loading ? (
          <>
            <style>{`@keyframes fp-spin { to { transform: rotate(360deg); } }`}</style>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", color: "#9e9e9e" }}>
              <div style={{
                width: "28px", height: "28px", borderRadius: "50%",
                border: "3px solid #e8f0fe", borderTopColor: "#1a73e8",
                animation: "fp-spin 0.8s linear infinite",
              }} />
              <span style={{ fontSize: "0.78rem" }}>Loading…</span>
            </div>
          </>
        ) : url && isPdf(attachment.originalName) ? (
          <iframe src={url} style={{ width: "100%", height: "100%", border: "none" }} title={attachment.originalName} />
        ) : url && isImage(attachment.originalName) ? (
          <img src={url} alt={attachment.originalName}
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", padding: "12px" }} />
        ) : (
          <div style={{ textAlign: "center", color: "#9e9e9e", padding: "32px" }}>
            <FontAwesomeIcon icon={icon} style={{ fontSize: "2.5rem", color, marginBottom: "12px" }} />
            <div style={{ fontSize: "0.82rem", marginBottom: "14px" }}>No preview available</div>
            <button
              onClick={() => downloadHandler(
                DocumentsConfig.endpoints.preview + attachment.fileId,
                attachment.originalName
              )}
              style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                padding: "7px 14px", background: "#1a73e8", color: "#fff",
                border: "none", borderRadius: "7px", fontWeight: 600,
                fontSize: "0.8rem", cursor: "pointer",
              }}
            >
              <FontAwesomeIcon icon={faDownload} /> Download
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilePreviewPanel;
